import { Component, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GridOptions } from 'ag-grid-community';
import { Subscription } from 'rxjs/internal/Subscription';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import SimpleContextMenuRenderer from 'src/shared/ag-grid-components/simple-context-menu';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleHelperService } from '../../../reusables/services/module-helper.service';
import { AgentStateDetailComponent } from '../../modals/agent-state-detail/agent-state-detail.component';
import { TasksModalComponent } from '../../modals/tasks-modal/tasks-modal.component';
import { CurrentAgentStatesService } from '../current-agent-states/current-agent-states.service';
import { ErrorCode } from "src/shared/models/error-code.model";
import { LeaderboardRankCellRendererComponent } from './leaderboard-rank-cell-renderer/leaderboard-rank-cell-renderer.component';
import { RankingModuleData, RankingRow } from './leaderboard.model';
import { LeaderboardService } from './leaderboard.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ObjectivesDetailsService } from '../../../reusables/objectives-details/objectives-details.service';
import { NumericType } from '../../../../shared/models/metric';
import { LeaderboardMyMetricComponent } from './leaderboard-my-metric/leaderboard-my-metric.component';
import { Subject } from 'rxjs';
import { MetricType } from 'src/app/reusables/metric-selector/metric-selector.model';
import { AgentStateDetailCXOneComponent } from '../../modals/agent-state-detail-cxone/agent-state-detail-cxone.component';
import { SuggestCoachingModalComponent } from '../../modals/suggest-coaching-modal/suggest-coaching-modal.component';
import { ContextMenuAwardsDialogComponent } from '../../awards/context-menu-awards-dialog/context-menu-awards-dialog.component';


@Component({
    selector: 'cv-leaderboard',
    templateUrl: './leaderboard.component.html',
    styleUrls: ['./leaderboard.component.scss']
})
export class LeaderboardComponent implements OnInit, OnDestroy {
    @ViewChild('leaderboardWidgetGrid') agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    public data: RankingModuleData = null;
    public filterValue = "";
    public showFilter = false;
    public searchPlaceholder = "";

    public gridOptions: GridOptions = null;
    public isGridReady: boolean = false;

    public noData = false;
    dataLoadSuccess = false;
    public noRankings = false;
    isAgentStateDetailModalOpen = false;

    private gridState: any = null;
    private isDataLoading = false;
    private isFirstLoad = true;
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    private stateSaveTimer: number;
    private setupGridTimer: number;
    private modalRef: NgbModalRef = null;
    private selectedRow = {};
    private subscriptions: Subscription[] = [];
    private locationVisible = false;
    private abortLoad: () => void = null;
    private isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');

    private dataSubject = new Subject<{ rowData: RankingRow[], noRankings: boolean, hasCurrentMetrics: boolean }>();
    public currentUserIndex = -1;
    public hasCurrentMetrics = false;
    private resizeTimer: any = null;
    private windowResizeListener: any = null;

    constructor(
        public configService: CvConfigurationService,
        private agGridHelperService: AgGridHelperService,
        private agContextMenuService: AgContextMenuService,
        private cvLocalizationService: CVLocalizationService,
        private service: LeaderboardService,
        private agentStateService: CurrentAgentStatesService,
        private navigationService: NavigationService,
        private ngbModal: NgbModal,
        private moduleHelperService: ModuleHelperService,
        private objectivesDetailsService: ObjectivesDetailsService,
        private ngZone: NgZone
    ) { }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.modalRef = null;
        this.subscriptions.forEach(sub => {
            sub.unsubscribe();
        });

        if (this.windowResizeListener) {
            window.removeEventListener('resize', this.windowResizeListener);
        }
        
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
    }

    public applyFilter() {
        if (this.agGrid?.api) {
            this.filterValue = this.filterValue;
            this.agGrid?.api?.setGridOption('quickFilterText', this.filterValue.trim());

            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.updateNoRowsMessage();
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    };

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.moduleManager.showLoader = true;
        if (this.configService.isCxOne) {
            this.moduleManager.setMaxDateRange(90);
        }
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Leaderboard_Module'),
            this.cvLocalizationService.GetSection('Common'),
            this.cvLocalizationService.GetSection("Objectives"),
        ];
        await Promise.all(localizationPromises);
        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: async settings => {
                await this.loadData(false, true);
                setTimeout(() => {
                    this.isMetricsCurrent();
                }, 500);
            },
            hasRefresh: true,
            onRefresh: async isAutoRefresh => {
                this.moduleManager.showLoader = !isAutoRefresh;
                const result = await this.loadData(isAutoRefresh);
                setTimeout(() => {
                    this.isMetricsCurrent();
                }, 500);
                return result;
            },
            canRefresh: () => !this.isDataLoading,
            onExport: format => this.service.LoadRankingModuleDataForExport(this.moduleKey, format)
        });

        this.moduleManager.registerEvents({
            onResize: () => {
                this.debouncedSizeColumnsToFit();
            },
            onFullscreenToggle: () => {
                this.debouncedSizeColumnsToFit(1000, !this.moduleManager.container.isFullscreen);
            }
        }); 
        
        this.ngZone.runOutsideAngular(() => {
            this.windowResizeListener = () => {
                this.ngZone.run(() => this.debouncedSizeColumnsToFit(250, true));
            };
            window.addEventListener('resize', this.windowResizeListener);
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.setupGrid();

        this.updateNoRowsMessage();

        if (!this.moduleManager.hasWriteAccess() && !this.configService.isCxOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch()
        });

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.gridState,
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }

                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);

            },
            autoLoad: true
        });

        if (this.configService.isCxOne && this.isFirstLoad) {
            this.moduleManager.addToolbarItem(LeaderboardMyMetricComponent, this.dataSubject);
        }

        this.subscriptions.push(this.agContextMenuService.agentDetailsClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey())
                return;
            const response = await this.agentStateService.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID, this.configService.isCxOne);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        }));

        this.subscriptions.push(this.agContextMenuService.createTaskClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));

        this.subscriptions.push(this.agContextMenuService.giveAwardClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }

            this.openAwardModal(params.moduleKey, params.groupData.groupID, params.groupData, this.configService.isCxOne)
        }));

        this.subscriptions.push(this.agContextMenuService.suggestCoachingClicked.subscribe(async params => {
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openSuggestCoachingModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));
    }

    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: false,
                filter: true,
                lockPinned: true,
                lockVisible: true,
                resizable: true,
                icons: this.configService.isCxOne ? {
                    sortAscending: `<div class="sortIcons">
                        <svg class="icon-10 fill-cxoneBlue">
                            <use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use>
                        </svg>
                        <svg class="icon-10 fill-sort">
                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                        </svg>
                    </div>`,
                    sortDescending: `<div class="sortIcons">
                        <svg class="icon-10 fill-sort">
                            <use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use>
                        </svg>
                        <svg class="icon-10 fill-cxoneBlue">
                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                        </svg>
                    </div>`,
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: `<svg class="icon-20 fill-iconMenu">
                        <use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use>
                    </svg>`,
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                } : null,
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            accentedSort: true,
            animateRows: true,
            rowHeight: this.configService.isCxOne ? 48 : 35,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressNoRowsOverlay: true,
            suppressContextMenu: true,
            context: {
                moduleManager: this.moduleManager,
                getMetric: (params) => params
            },
            components: {
                agentNameRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
                groupHeaderComponent: this.agGridHelperService.GroupHeaderComp,
                LeaderboardRankCellRendererComponent,
                metricMenuRenderer: SimpleContextMenuRenderer,
            },
            getMainMenuItems: (params) => this.agGridHelperService.hideResetColumnsMenuItem(params, this.moduleManager.hasWriteAccess()),
            onGridReady: event => {
                if (this.data?.Error === ErrorCode.NoError) {
                    this.updateColumnDefs();
                    this.agGrid?.api.setGridOption('rowData', this.data.RowData);
                }
                this.isGridReady = true;
                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreTableState(); //Needed to restore columns size saved on the gridState.
                this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.

                // If there is no grid state (aka, the user has never saved a state before), size the columns to fit the grid.
                if (!this.gridState) {
                    event.api.sizeColumnsToFit();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save

                if (!this.gridState) {
                    this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
                }
                
            },
            getRowId: ({ data: rowData }) => rowData.Group.groupID,
            getRowClass: ({ data: rowData }) => {
                if (!this.configService.isCxOne || !rowData.IsCurrentUser) return '';
                return 'current-user-row';
            },
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.queueStateSave();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreTableState();
                }
            },
            onFilterChanged: () => {
                this.queueStateSave();
                setTimeout(() => {
                    this.isMetricsCurrent();
                }, 500);
            },
            onSortChanged: () => this.queueStateSave(),
            onColumnMoved: () => this.queueStateSave(),
            onColumnPinned: () => this.queueStateSave(),
            onColumnVisible: () => this.queueStateSave(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.queueStateSave();
                }
            },
            // external filtering (our search bar)
            isExternalFilterPresent: () => !!this.filterValue,
            doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue),

            // grid events
            getContextMenuItems: params => this.getContextMenuItem(params),
        }, {
            enableCopy: !this.configService.isCxOne
        });
    }

    private restoreTableState() {
        if (this.gridState && this.gridState.columns && this.agGrid?.api) {
            this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
        }
    };

    private updateColumnDefs() {
        if (!this.agGrid?.api)
            return;

        this.gridOptions.columnDefs = [];
        if (this.data.RowData.length !== 0) {
            this.gridOptions.columnDefs.push({
                field: 'Place',
                headerName: this.cvLocalizationService.getCachedText('Leaderboard_Module', 'Rank_Header'),
                maxWidth: 100,
                cellStyle: this.configService.isCxOne ? '' : {
                    display: 'flex',
                    'justify-content': 'center',
                    'flex-flow': 'column',
                } as any,
                filter: 'agTextColumnFilter',
                resizable: true,
                cellRenderer: this.configService.isCxOne ? 'LeaderboardRankCellRendererComponent' : '',
                cellRendererParams: this.configService.isCxOne ? { noRankings: this.noRankings } : '',
                menuTabs: ['generalMenuTab', 'filterMenuTab'],
                suppressMenu: this.isSharedURL,
            });
            this.gridOptions.columnDefs.push({
                field: 'Group',
                headerName: this.cvLocalizationService.getCachedText('Leaderboard_Module', 'Name_Header'),
                colId: 'Name',
                cellRenderer: 'agentNameRenderer',
                filter: 'agTextColumnFilter',
                type: 'groupColumn',
                filterValueGetter: params => params.data.Group.groupName,
                resizable: true,
                menuTabs: ['generalMenuTab', 'filterMenuTab'],
                suppressMenu: this.isSharedURL,
            });
            this.gridOptions.columnDefs.push({
                field: 'MetricData',
                headerName: this.data.MetricCustomName && this.data.MetricCustomName != "" ? this.data.MetricCustomName + "*" : this.data.MetricName,
                headerTooltip: this.data.MetricCustomName && this.data.MetricCustomName != "" ?
                    this.cvLocalizationService.getCachedText("Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + this.data.MetricName : null,
                maxWidth: 200,
                cellRenderer: 'metricMenuRenderer',
                cellRendererParams: { isCXOne: this.configService.isCxOne, isLeaderboard: true },
                type: 'metricColumn',
                resizable: true,
                menuTabs: ['generalMenuTab', 'filterMenuTab'],
                suppressMenu: this.isSharedURL,
            });

            this.agGrid.api.setGridOption('columnDefs', this.gridOptions.columnDefs);
            if (this.gridState == null || this.gridState.columns.map(c => c.width).filter(w => w != null).length === 0)
                this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
            else
                this.restoreColumnState();
        }
    }

    private async queueLaunchCoaching(params): Promise<void> {
        const baseUrl = this.navigationService.cxoneTokenUrls?.niceBaseUrl;
        await this.moduleHelperService.launchCoachingClicked(baseUrl, params.node.data.Group.groupID, this.data.MetricName);
    }

    private getContextMenuItem(params) {
        if (typeof params.value === 'string') {
            return this.agGridHelperService.getContextMenuItems(params);
        }
        if (this.configService.isCxOne) {
            return [{
                name: this.cvLocalizationService.getCachedText("Leaderboard_Module", "Launch_Coaching", "Launch Coaching"),
                action: () => this.queueLaunchCoaching(params),
                disabled: false,
                icon: `<svg class="icon-16 fill-actionCellIcon">
                            <use href="../App_Themes/CXone/icons/widgets/launch-coaching.svg#launch-coaching"></use>
                       </svg>` ,
            }];
        } else {
            return [{
                name: this.cvLocalizationService.getCachedText("Objectives", "Objectives_Details", "Objectives Details"),
                action: () => this.objectivesDetailsModalClicked(params),
                icon: `<svg class="icon-16" style="fill: #6a7480;">
                            <use href="../App_Themes/clearview-default/icons/dashboard/gauge-range.svg#gauge-range"></use>
                       </svg>`,
            }];
        }
    }

    private objectivesDetailsModalClicked(params: any) {
        let value = angular.copy(params.value);

        if (this.data.NumericType === NumericType.Percent)
            value.MetricValue *= 100;

        this.objectivesDetailsService.openObjectivesDetailsModal(
            value,
            params.node.data.Group,
            params.column.colDef.headerName,
        );
    }

    private async loadData(isAutoRefresh: boolean, isFromSettings = false) {
        if (this.isDataLoading) { // cancel current load
            this.abortLoad?.();
            this.abortLoad = null;
        }
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        let aborted = false;
        const abort: Promise<void> = new Promise((resolve, reject) => this.abortLoad = () => resolve());
        try {
            this.data = await this.service.LoadRankingModuleData(this.moduleKey, isAutoRefresh, this.isFirstLoad, abort);
            this.noData = this.data.RowData.length === 0;
            //only execution of Cxone
            this.checkNullMetricValues(this.data);
            this.checkNoRankingMetricValues(this.data);
            if (this.agGrid?.api) {
                if (this.gridOptions.columnDefs.length === 0 || !this.configService.isCxOne || isFromSettings) {
                    this.updateColumnDefs();
                }
                this.agGrid?.api.setGridOption('rowData', this.data.RowData);
                this.agGrid?.api.redrawRows();
            }

            this.isFirstLoad = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            if (err instanceof DOMException && err.name === "AbortError")
                aborted = true;
            else this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            if (!aborted) {
                this.dataLoadSuccess = !this.noData;
                this.moduleManager.showLoader = false;
                this.isDataLoading = false;
                this.moduleManager.container.showDateRange = true;
            }
        }
    }

    private queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        this.stateSaveTimer = window.setTimeout(async () => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500/*ms*/);
    };

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.gridOptions.api || this.stateRestoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid.api);
        if (tableState != null) {
            this.gridState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    };

    restoreColumnState() {
        if (!this.gridState || !this.agGrid.api) {
            return;
        }

        this.agGridHelperService.loadGridState(this.agGrid.api, this.gridState);
    };

    private toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    };

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.configService.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
        }, 200);
    }

    private updateNoRowsMessage() {
        if (this.configService.isCxOne) {
            this.gridOptions.overlayNoRowsTemplate = `<div class="error-container" style="padding-top: 48px;">
                <svg class="mb-2">
                    <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                </svg>
                <p class="cxone-error-text">
                    ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                </p>
            </div>`;
        } //This message will display just for CXOne.
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    private openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any) {
        if (this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;

        this.modalRef = this.configService.isCxOne
            ? this.ngbModal.open(AgentStateDetailCXOneComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: 'cx-one-ui' })
            : this.ngbModal.open(AgentStateDetailComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openAwardModal(moduleKey: string, groupID: number, selectedRow: any, _isCxone: boolean) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(ContextMenuAwardsDialogComponent,
            { size: "xl", centered: true, scrollable: true, modalDialogClass: 'give-award-dialog' });
        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    openSuggestCoachingModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent,
            { size: "md", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: SuggestCoachingModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    /**
     * Function to check if the MetricDisplayValue element is null, if null change to 'N/A'. Only execution of Cxone
     * @param data Array from service.LoadRankingModuleData
     */
    private checkNullMetricValues(data: any): void {
        if (this.configService.isCxOne) {
            for (const element of data.RowData) {
                if (element.MetricData.MetricDisplayValue == null) {
                    element.MetricData.MetricDisplayValue = 'N/A';
                }
            }
        }
    }


    /**
     * Function to check if each array element is 0, null, '00:00:00', '0%', '0', if true the 'noRankings' flag is set to true. Only execution of Cxone
     * @param data Array from service.LoadRankingModuleData
     */
    private checkNoRankingMetricValues(data: any): void {
        if (this.configService.isCxOne) {
            const allZeroOrNullValuesFunction = (currentValue) => currentValue.MetricData.MetricDisplayValue === 0 ||
                currentValue.MetricData.MetricDisplayValue == null || currentValue.MetricData.MetricDisplayValue === '00:00:00' ||
                currentValue.MetricData.MetricDisplayValue === '0%' || currentValue.MetricData.MetricDisplayValue === '0';
            if (data.RowData.length > 0) {
                if (data.RowData.every(allZeroOrNullValuesFunction)) {
                    this.noRankings = true;
                } else {
                    this.noRankings = false;
                }
            }
        }
    }

     /**
     * Determines if the current metrics require special handling and updates relevant states
     * Always uses actual data displayed in the grid view
     */
    private isMetricsCurrent(): void {
        if (!this.configService.isCxOne || !this.agGrid?.api) return;
        
        const settings = this.moduleManager?.moduleSettings;
        const selectedMetrics = settings?.selectedMetrics || [];
        
        if (selectedMetrics.length === 0) {
            settings.hideDatePicker = true;
            return;
        }
        
        const hasCurrentMetrics = selectedMetrics.some(metric => 
            metric.MetricType === MetricType.CurrentStatesMetricData || 
            metric.MetricType === MetricType.Snapshot
        );
        
        // Get only the data that's actually visible in the grid
        const visibleRows = [];
        this.agGrid.api.forEachNodeAfterFilterAndSort(node => {
            if (node.data) visibleRows.push(node.data);
        });
        
        // Check if current user is in visible rows
        const isCurrentUser = visibleRows.some(row => row.IsCurrentUser);
        
        // Update service state
        this.service.setLeaderboardStates(hasCurrentMetrics, isCurrentUser, this.moduleKey);
        
        this.dataSubject.next({ 
            rowData: visibleRows,
            noRankings: this.noRankings ?? false, 
            hasCurrentMetrics 
        });
        
        // Update settings and component state
        settings.hideDatePicker = hasCurrentMetrics && !isCurrentUser;
        this.hasCurrentMetrics = hasCurrentMetrics;
    }

    private debouncedSizeColumnsToFit(delay: number = 250, reset = false): void {
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
        
        this.resizeTimer = setTimeout(() => {
            if (this.isGridReady && this.agGrid?.api) {
                if (reset) {
                    this.restoreTableState();
                } else {
                    this.agGrid.api.sizeColumnsToFit();
                }
            }
        }, delay);
    }
}
